import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase/firebase';
import { useStore } from '../../context/StoreContext';

const PIN_SESSION_KEY = 'analytics_pin_ts';
const PIN_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes

/**
 * AnalyticsPinGate
 *
 * Wraps the Analytics dashboard with a 4-digit PIN prompt.
 * PIN is stored in Firestore config/analytics doc.
 * Session unlocks for 15 minutes (stored in sessionStorage).
 * All PIN attempts are audit-logged with IP + optional GPS.
 */
export default function AnalyticsPinGate({ children }) {
  const { activeStoreId } = useStore();
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [correctPin, setCorrectPin] = useState(null);
  const inputRefs = useRef([]);

  // Check if PIN session is still valid
  useEffect(() => {
    const ts = sessionStorage.getItem(PIN_SESSION_KEY);
    if (ts && Date.now() - parseInt(ts, 10) < PIN_EXPIRY_MS) {
      setUnlocked(true);
    }
    // Fetch the PIN from Firestore config
    fetchPin();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPin = async () => {
    try {
      const configRef = doc(db, 'config', 'analytics');
      const configSnap = await getDoc(configRef);
      if (configSnap.exists()) {
        setCorrectPin(configSnap.data().pin || null);
      }
    } catch (err) {
      console.error('Error fetching analytics PIN config:', err);
    } finally {
      setLoading(false);
    }
  };

  const getUserInfo = useCallback(() => {
    const user = auth.currentUser;
    return {
      uid: user?.uid || 'unknown',
      name: user?.displayName || user?.email || 'unknown',
    };
  }, []);

  const getIpAddress = async () => {
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      const data = await res.json();
      return data.ip;
    } catch {
      return null;
    }
  };

  const getLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }),
        () => resolve(null),
        { timeout: 5000, maximumAge: 300000 }
      );
    });
  };

  const writeAuditLog = useCallback(
    async (action) => {
      try {
        const userInfo = getUserInfo();
        const [ip, location] = await Promise.all([
          getIpAddress(),
          getLocation(),
        ]);

        await addDoc(collection(db, 'audit_log'), {
          user: userInfo.name,
          uid: userInfo.uid,
          role: 'authenticated',
          store_id: activeStoreId || 'unknown',
          action,
          ip: ip || null,
          location: location || null,
          timestamp: serverTimestamp(),
        });
      } catch (err) {
        console.error('Error writing audit log:', err);
      }
    },
    [activeStoreId, getUserInfo]
  );

  const handleDigitChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-advance to next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 4 digits entered
    if (value && index === 3 && newPin.every((d) => d !== '')) {
      submitPin(newPin.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      const newPin = pasted.split('');
      setPin(newPin);
      submitPin(pasted);
    }
  };

  const submitPin = async (enteredPin) => {
    if (enteredPin === correctPin) {
      sessionStorage.setItem(PIN_SESSION_KEY, Date.now().toString());
      setUnlocked(true);
      writeAuditLog('analytics_pin_success');
    } else {
      setError('PIN incorrecto');
      setPin(['', '', '', '']);
      inputRefs.current[0]?.focus();
      writeAuditLog('analytics_pin_failure');
    }
  };

  // If no PIN is configured, skip the gate
  if (loading) {
    return (
      <div className="pin-gate-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!correctPin || unlocked) {
    return children;
  }

  return (
    <div className="pin-gate-overlay" data-testid="pin-gate">
      <div className="pin-gate-card">
        <div className="pin-gate-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
        </div>
        <h2 className="pin-gate-title">Analytics</h2>
        <p className="pin-gate-subtitle">Ingresa el PIN de 4 digitos</p>

        <div className="pin-inputs" onPaste={handlePaste}>
          {pin.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="pin-digit"
              autoFocus={i === 0}
              aria-label={`Digito ${i + 1}`}
            />
          ))}
        </div>

        {error && (
          <p className="pin-error" role="alert">
            {error}
          </p>
        )}

        <p className="pin-gate-hint">
          El acceso se mantiene por 15 minutos
        </p>
      </div>
    </div>
  );
}
